
import { defineComponent } from 'vue';
import { getCookie, validateEmail } from '@/utils';

const FORM_ENDPOINT = '/.rest/api/v1/cta';
const FORM_ENDPOINT_NEW = '/.rest/api/v1/forms/submit';

export default defineComponent({
    props: {
        fieldset: {
            type: String,
            default: 'basic'
        },
        useNewEndpoint: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            notvalid: false,
            submitted: false,
            success: false,
            loading: false,
            fileSizeError: false,
            fileCountError: false,
            captchaSolved: false,
            files: []
        };
    },
    methods: {
        async submit({ target }) {
            this.notvalid = false;
            const form: HTMLFormElement =
                target.tagName.toLowerCase() === 'form' ? target : target.form;
            const formInputs = target.elements;

            for (let i = 0; i < formInputs.length; i++) {
                if (
                    (formInputs[i].required && !formInputs[i].value) ||
                    (formInputs[i].type === 'email' && validateEmail(formInputs[i].value)) ||
                    (formInputs[i].type === 'checkbox' &&
                        formInputs[i].required &&
                        !formInputs[i].checked) ||
                    (formInputs[i].type === 'radio' &&
                        formInputs[i].required &&
                        !form.querySelector(`input[name="${formInputs[i].name}"]:checked`))
                ) {
                    this.notvalid = true;
                }
            }

            if (!this.notvalid && !this.fileCountError && !this.fileSizeError) {
                this.loading = true;
                this.submitted = false;

                const fd: FormData = new FormData(form);
                const utmParams = this.getUTMParams();

                let topicExternalSource = fd.get('topicExternalSource') || '';

                // set distributor field
                if (this.fieldset === 'pro') {
                    topicExternalSource = `${fd.get('distributor')}${topicExternalSource}`;
                }

                // Append each UTM parameter if it exists
                const utmKeys = [
                    'utm_source',
                    'utm_medium',
                    'utm_campaign',
                    'utm_term',
                    'utm_content'
                ] as const;

                utmKeys.forEach(key => {
                    const value = utmParams[key] || getCookie(key); // check in params or cookie if needed
                    if (value) {
                        topicExternalSource += `, ${key}: ${value}`;
                    }
                });

                fd.set('topicExternalSource', topicExternalSource);

                if (this.files.length) {
                    fd.set('filename', this.files.map(x => x.name).join(','));
                    this.files.forEach((file: File, index: number) => {
                        fd.set(`file${index + 1}`, file);
                    });
                    fd.delete('file');
                }

                const endpoint = this.useNewEndpoint
                    ? `${FORM_ENDPOINT_NEW}/${this.$siteName}/${this.$lang}`
                    : `${FORM_ENDPOINT}/${this.$region ? this.$region : this.$siteName}/${
                          this.$lang
                      }`;

                try {
                    const res = await fetch(`${this.$contextPath}${endpoint}`, {
                        method: 'POST',
                        body: fd
                    });

                    if (this.useNewEndpoint) {
                        this.success = res.status === 200;
                    } else {
                        const data = await res.json();
                        this.success = data.success;
                    }
                } catch (e) {
                    this.success = false;
                }
                this.submitted = true;
                this.loading = false;
            }
        },
        getUTMParams() {
            const urlParams = new URLSearchParams(window.location.search);
            return {
                utm_source: urlParams.get('utm_source') || '',
                utm_medium: urlParams.get('utm_medium') || '',
                utm_term: urlParams.get('utm_term') || '',
                utm_content: urlParams.get('utm_content') || ''
            };
        },
        selectFile({ target }: { target: HTMLInputElement }) {
            const files = target.files;
            // add files
            for (let i = 0; i < files.length; i++) {
                this.files.push(files[i]);
            }
            this.udpateFile();
        },
        removeFile(fileToRemove) {
            this.files = this.files.filter(file => file !== fileToRemove);
            // clean input so it is possible to add the same file again
            const inputFile = document.getElementById('file') as HTMLInputElement;
            if (inputFile) {
                inputFile.value = '';
            }
            this.udpateFile();
        },
        udpateFile() {
            this.fileCountError = this.files.length > 3;
            let fileSize = 0;
            for (let i = 0; i < this.files.length; i++) {
                fileSize += this.files[i].size;
            }
            this.fileSizeError = fileSize > 10 * 1024 * 1024;
        },
        onCaptchaSolved(success: boolean) {
            this.captchaSolved = success;
        }
    }
});
